import type { VerificationTranslations } from "../interfaces/verification";

export const verificationTranslations: VerificationTranslations = {
  // Shell / stepper / shared actions
  step_intro: "Start",
  step_consent: "Consent",
  step_data: "Details",
  step_company: "Company",
  step_capture: "Capture",
  step_result: "Result",
  stepper_progress: "{{current}}/{{total}}",
  trust_footer: "Secure, encrypted verification by Coloca",
  button_start: "Start",
  button_continue: "Continue",
  button_back: "Back",
  button_retry: "Retry",
  button_close: "Close",
  button_continue_later: "Continue later",

  // Intro — KYC (person)
  kyc_intro_title: "Verify your identity",
  kyc_intro_subtitle: "It's quick and secure. It will take about 2 minutes.",
  kyc_intro_needs_title: "You'll need:",
  kyc_intro_need_document: "Your ID document",
  kyc_intro_need_selfie: "To take a selfie",
  kyc_intro_security: "Your data is encrypted and never shared with third parties without your consent.",
  kyc_intro_cta: "Start",

  // Intro — KYB (company)
  kyb_intro_title: "Verify your company",
  kyb_intro_subtitle: "It will take between 5 and 7 minutes.",
  kyb_intro_needs_title: "You'll need:",
  kyb_intro_need_company: "Your company's tax details and those of its legal representative",
  kyb_intro_need_documents:
    "Have the incorporation document and tax ID ready; the verifier will ask for them during capture.",
  kyb_intro_save_note: "You can save and continue later.",
  kyb_intro_cta: "Start",

  // Resume banner
  resume_title: "You have a verification in progress",
  resume_description: "We'll pick up right where you left off.",
  resume_cta: "Continue",

  // Consent
  consent_title: "Your consent",
  consent_kyc_required: "I authorize the processing of my biometric data to verify my identity.",
  consent_kyb_required: "I declare that I am authorized to represent the company.",
  consent_terms_label: "I accept the Terms and Conditions.",
  consent_terms_link: "View Terms and Conditions",
  consent_terms_new_tab: "(opens in a new tab)",
  consent_required: "Tick this box to continue.",
  consent_cta: "I accept and continue",

  // Data (KYC) + confirm variant
  data_title: "Document details",
  data_subtitle: "Confirm they match your ID document.",
  confirm_title: "Are these details correct?",
  confirm_cta_confirm: "Confirm and continue",
  confirm_cta_edit: "Edit details",
  confirm_field_name: "Name",
  confirm_field_country: "Country",
  confirm_field_document_type: "Document type",

  // Company (KYB) + dedupe variant
  company_title: "Company details",
  company_block_fiscal: "Tax identity",
  company_block_fiscal_badge: "Validated and shared",
  company_block_representative: "Legal representative",
  company_block_contact: "Contact",
  company_dedupe_note: "Country, tax type and number uniquely identify the company.",
  company_verified_title: "This company is already registered and verified",
  company_verified_description: "We reuse its tax identity; these fields are locked.",
  company_verified_cta_documents: "View documents",
  company_verified_cta_back: "Back to the form",

  // Capture — KYC (single embedded session: document + selfie/liveness)
  capture_doc_title: "Verify your identity",
  capture_doc_instruction: "Follow the steps to photograph your document and take a selfie.",
  capture_doc_hint_light: "Good lighting",
  capture_doc_hint_complete: "Document and face visible",
  capture_doc_hint_glare: "No glare",

  // Capture — company
  capture_company_title: "Company verification",
  capture_company_instruction:
    "The verifier will ask for the incorporation document, tax ID and whatever the level requires.",
  capture_company_hint_incorporation: "Incorporation document",
  capture_company_hint_taxid: "Tax ID",

  // Capture — states
  capture_loading: "Starting verification…",
  capture_error_title: "We couldn't start the camera",
  capture_error_description: "Check your connection and try again.",
  capture_denied_title: "Camera permission blocked",
  capture_denied_description: "Enable camera access in your browser to continue.",
  capture_action_retry: "Retry",
  capture_action_allowed: "I've allowed it",

  // Submit (KYB ready-to-send variant)
  submit_title: "Ready to send",
  submit_summary_company: "Company",
  submit_summary_taxid: "Tax ID",
  submit_summary_representative: "Legal representative",
  submit_cta: "Send for review",
  submit_cta_back: "Back to capture",

  // Processing
  processing_kyc_title: "We're verifying your identity…",
  processing_kyb_title: "We're verifying your company…",
  processing_note: "You can close this window; we'll email you when it's ready.",

  // Result
  result_verified_title: "Verification complete",
  result_verified_description: "You're all set to continue.",
  result_verified_description_external:
    "Your verification has been recorded. Whoever requested it will get the confirmation.",
  result_verified_cta: "Go to home",
  result_verified_cta_done: "Done",
  result_verified_redirect_hint: "Taking you home in {{seconds}}s.",
  result_verified_redirect_cancel: "Stay here",
  result_verified_redirect_announce:
    "We'll take you home in a few seconds. Use the button to go now, or cancel the redirect.",
  verified_close_window_note: "There's nothing else to do — you can close this window.",

  // Welcome after approval
  welcome_eyebrow: "Verification approved",
  welcome_title: "You're ready to use the platform!",
  welcome_description_kyc:
    "We approved your identity verification, so everything that depended on it is now enabled.",
  welcome_description_kyb:
    "We approved your company's verification, so everything that depended on it is now enabled.",
  welcome_choose: "Where would you like to start?",
  welcome_shortcuts_label: "Shortcuts",
  welcome_close: "Close",
  welcome_shortcut_payins_description: "Create a payment request and share it with whoever is paying you.",
  welcome_shortcut_payouts_description: "Send money to bank accounts and keys.",
  welcome_shortcut_cards_description: "Issue cards for your team or your customers.",
  welcome_shortcut_wallets_description: "Check your balances and activity in each currency.",
  welcome_shortcut_security_title: "Security",
  welcome_shortcut_security_description: "Choose how you'll confirm payments and card movements.",
  welcome_shortcut_crypto_description: "Buy USDT with your peso wallet balance.",
  welcome_shortcut_payout_batches_description: "Pay many people at once with a spreadsheet.",
  welcome_shortcut_clients_description: "Register the people and companies you work with.",
  result_review_title: "We're reviewing your verification",
  result_review_description: "We'll email you when we have the result.",
  result_review_cta: "Back",
  result_rejected_title: "We couldn't complete the verification",
  result_rejected_description: "Check the reason and try again.",
  result_rejected_cta: "Retry verification",
  result_expired_title: "This link has expired",
  result_expired_description: "Request a new one to continue.",
  result_expired_cta: "Request a new one",

  // Transversal states
  invalid_title: "Invalid link",
  invalid_description: "The verification link is invalid or no longer available.",
  already_title: "You're already verified",
  already_description: "Your verification was completed on {{date}}.",
  mobile_title: "Continue on your phone",
  mobile_description: "Scan the code to finish the capture on your phone.",
  error_generic_title: "Something went wrong",
  error_generic_description: "We couldn't complete the operation. Please try again.",

  // Rejection reasons
  rejection_document_quality: "The document photo isn't clear. Try again with good lighting.",
  rejection_document_invalid: "The document is invalid or unrecognized.",
  rejection_document_incomplete: "Document details are missing. Upload the full document.",
  rejection_selfie_mismatch: "The selfie didn't match the document. Try again with good lighting.",
  rejection_data_mismatch: "The details don't match the document.",
  rejection_duplicate: "A verification already exists for this identity.",
  rejection_compliance: "We couldn't approve the verification. Please contact support.",
  rejection_additional_info_required: "We need additional information to continue.",
  rejection_generic: "We couldn't complete the verification. Please try again.",

  // Documents (§6B)
  documents_title: "Documents",
  documents_signed_note: "Downloads use temporary signed links.",
  documents_download: "Download",
  documents_empty_title: "No documents yet",
  documents_empty_description: "They'll appear once the verification is complete.",

  // Onboarding (marco A)
  onboarding_title: "Account verification",
  onboarding_description: "Complete your account verification to start operating.",
  banner_cta: "Verify now",
  banner_cta_resume: "Continue verification",
  banner_view_details: "View details",
  banner_blocked_prefix: "Until your account is verified you cannot:",
  banner_in_review_title: "Your verification is under review",
  banner_in_review_description: "We will email you as soon as we have the result.",
  banner_rejected_title: "We could not approve your verification",
  banner_expired_title: "Your verification expired",
  banner_expired_description: "Start the verification again to keep operating.",

  // Onboarding — KYC vs KYB choice
  choose_title: "How do you operate on Coloca?",
  choose_description:
    "Choose how you want to verify your account. The verification you choose is the one that enables your operations.",
  choose_person_title: "As a natural person",
  choose_person_title_mx: "As a natural person",
  choose_person_description: "We will verify your identity with your ID document and a selfie (KYC).",
  choose_business_title: "As a company",
  choose_business_description: "We will verify the company and its legal representative (KYB).",
  choose_note: "This choice is made once: it is fixed when the identity is created.",
  choose_cta: "Continue",
  replace_warning_title: "You are about to verify your account with another identity",
  replace_warning_description:
    "Your current verification will no longer apply. Choose how you will operate from now on; sending money stays blocked until the new verification is approved.",

  // INTERNAL — account verification admin
  admin_title: "Account verification",
  admin_description: "An organization's verification status and the identity-change cooldown waiver.",
  admin_scope_label: "Organization",
  admin_scope_placeholder: "Search by name, email or id",
  admin_scope_loading: "Loading organizations…",
  admin_scope_empty: "No results",
  admin_scope_searching: "Searching…",
  admin_scope_search_error: "We could not search organizations.",
  admin_scope_catalog_error: "We could not load the organizations.",
  admin_scope_catalog_truncated: "Showing the first organizations; search to reach the rest.",
  admin_scope_global_option: "No organization",
  admin_scope_global_hint: "Pick the organization that asked for the change.",
  admin_scope_org_hint: "You are looking at this organization's verification.",
  admin_pick_org: "Pick an organization to see its verification.",
  admin_load_error: "We could not load this organization's verification.",
  admin_type_label: "Type",
  admin_status_label: "Status",
  admin_change_label: "Identity change",
  admin_change_open: "May change identity now.",
  admin_change_blocked: "Blocked until {{date}} (a verified identity was replaced recently).",
  admin_waive: "Waive cooldown",
  admin_waive_confirm_title: "Waive the cooldown?",
  admin_waive_confirm_description:
    "The organization will be able to verify its account with another identity immediately. It is recorded and operations are alerted on Discord.",
  admin_waive_cancel: "Cancel",
  admin_waive_done: "Cooldown waived.",
  admin_retry: "Retry",

  // Capability matrix
  capability_payouts_create: "Send payouts",
  capability_payins_create: "Receive payments",
  capability_wallets_recharge: "Recharge wallets",
  capability_swaps_create: "Convert between wallets",
  capability_cards_issue: "Issue cards",

  // Dispersions gate
  payouts_blocked_title: "Verify your account to send money",
  payouts_blocked_description:
    "Payouts are enabled once your account verification is approved. You can resume it where you left off.",
  payouts_blocked_in_review_description:
    "Your verification is under review. You will be able to send money once it is approved.",
  payouts_blocked_back_wallets: "Back to wallets",
  payouts_blocked_start_description:
    "Choose whether you operate as a natural person or as a company and complete the verification to send money.",

  // Status pill labels
  status_pending: "Pending",
  status_in_review: "In review",
  status_verified: "Verified",
  status_rejected: "Rejected",
  status_expired: "Expired",

  // Marco C — identity verification section / documents
  verify_action: "Start verification",
  verify_dialog_title: "Verification",
  link_generate: "Generate link",
  link_copied: "Link copied to clipboard",
  documents_report_label: "Verification report",
  photo_guide_eyebrow: "Before you take the photos",
  photo_guide_title: "This is how your photos should look",
  photo_guide_lead:
    "If they look like this, your verification is approved on its own. If not, it goes to manual review and can take weeks.",
  photo_guide_tip_light_title: "Good light",
  photo_guide_tip_light_body: "From the front and even, with no flash, glare or shadows.",
  photo_guide_tip_place_title: "A clear spot",
  photo_guide_tip_place_body: "A plain wall behind you and a dark table for the document.",
  photo_guide_tip_document_title: "Your physical document",
  photo_guide_tip_document_body:
    "Original and not expired. Photocopies and digital versions are not accepted.",
  photo_guide_document_title: "Your document",
  photo_guide_document_caption:
    "All four corners in view and everything readable. Front and back in separate photos; a passport, open, in a single photo.",
  photo_guide_selfie_title: "Your selfie",
  photo_guide_selfie_caption: "Only your face, centered and facing the camera, with your eyes open.",
  photo_guide_example_ok: "Like this",
  photo_guide_example_corner: "A corner out of the photo",
  photo_guide_example_glare: "Glare or flash",
  photo_guide_example_angle: "At an angle or skewed",
  photo_guide_example_blurry: "Blurry",
  photo_guide_example_contrast: "No contrast with the background",
  photo_guide_example_offcenter: "Off-center or cut off",
  photo_guide_example_tilted: "Head tilted",
  photo_guide_example_glasses: "Glasses or a cap",
  photo_guide_example_holding: "Holding the document",
  photo_guide_example_crowd: "Someone else in the photo",
  photo_guide_rejected_note: "If a photo is rejected, that is fine: take a new one and upload it again.",
  photo_guide_cta: "Start",
};
